import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Briefcase, TrendingUp, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Opportunity } from '@/types/opportunity';

interface RelatedOpportunitiesProps {
  member?: any;
}

const RelatedOpportunities = ({ member }: RelatedOpportunitiesProps) => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelatedOpportunities = async () => {
      if (!member?.skills || member.skills.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('opportunities')
          .select('*')
          .eq('status', 'open')
          .order('created_at', { ascending: false })
          .limit(3);

        if (error) throw error;

        // Filter by skills match
        const filtered = (data || []).filter(opp => 
          opp.required_skills?.some(skill => 
            member.skills.some((memberSkill: string) => 
              memberSkill.toLowerCase().includes(skill.toLowerCase()) ||
              skill.toLowerCase().includes(memberSkill.toLowerCase())
            )
          )
        );

        setOpportunities(filtered.slice(0, 3));
      } catch (error) {
        console.error('Error fetching related opportunities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedOpportunities();
  }, [member]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Opportunités liées à vos connexions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-muted/50 rounded-lg animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (opportunities.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary" />
          Opportunités liées à votre réseau
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {opportunities.map((opp) => (
          <div
            key={opp.id}
            className="p-3 border border-border/50 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
            onClick={() => navigate(`/opportunities?id=${opp.id}`)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-sm truncate">{opp.title}</h4>
                <p className="text-xs text-muted-foreground mt-1">{opp.company}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="secondary" className="text-xs">
                    {opp.type}
                  </Badge>
                  {opp.deadline && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {new Date(opp.deadline).toLocaleDateString('fr-FR')}
                    </div>
                  )}
                </div>
              </div>
              <TrendingUp className="h-4 w-4 text-primary shrink-0" />
            </div>
          </div>
        ))}

        <Button
          variant="outline"
          className="w-full mt-2"
          onClick={() => navigate('/opportunities')}
        >
          Voir toutes les opportunités
        </Button>
      </CardContent>
    </Card>
  );
};

export default RelatedOpportunities;
